import { z } from 'zod';

import { languageKeySchema } from '~/shared/internationalization';

import { idSchema } from './id';
import { paginationSchema } from './pagination';

export const skillTypeSchema = z.union([z.literal('hard'), z.literal('language'), z.literal('soft')]);

export type SkillType = z.infer<typeof skillTypeSchema>;

const skillTypesSchema = z.union([z.literal('language'), z.literal('hard'), z.literal('soft')]);

export const languageSkillProficiencyLevelSchema = z.union([
    z.literal(1),
    z.literal(2),
    z.literal(3),
    z.literal(4),
    z.literal(5),
    z.literal(6),
]);

export const languageSkillProficiencyWithUnknownLevelSchema = z.union([
    ...languageSkillProficiencyLevelSchema.options,
    z.literal(0),
]);

export type LanguageSkillProficiencyWithUnknownLevel = z.infer<typeof languageSkillProficiencyWithUnknownLevelSchema>;

export const DefaultLanguageProficiencyWithUnknownLevel = languageSkillProficiencyWithUnknownLevelSchema.parse(3);

export const hardSoftSkillProficiencyLevelSchema = z.union([
    z.literal(1),
    z.literal(2),
    z.literal(3),
    z.literal(4),
    z.literal(5),
]);

export const hardSoftSkillProficiencyWithUnknownLevelSchema = z.union([
    ...hardSoftSkillProficiencyLevelSchema.options,
    z.literal(0),
]);

export type HardSoftSkillProficiencyWithUnknownLevel = z.infer<typeof hardSoftSkillProficiencyWithUnknownLevelSchema>;

export const DefaultHardSoftProficiencyWithUnknownLevel = hardSoftSkillProficiencyWithUnknownLevelSchema.parse(3);

export const skillProficiencyLevelSchema = hardSoftSkillProficiencyWithUnknownLevelSchema.or(
    languageSkillProficiencyWithUnknownLevelSchema,
);

export type SkillProficiencyLevel = HardSoftSkillProficiencyWithUnknownLevel | LanguageSkillProficiencyWithUnknownLevel;

const endorsementSchema = z.object({
    endorsedByCertificate: z.string().array().optional(),
    endorsedByCourse: z.string().array().optional(),
    endorsedByEducation: z.string().array().optional(),
    endorsedByProject: z.string().array().optional(),
    endorsedByPublication: z.string().array().optional(),
    endorsedByWork: z.string().array().optional(),
});

export type Endorsement = z.infer<typeof endorsementSchema>;

export const skillTitleTranslationSchema = z.object({
    language: languageKeySchema,
    title: z.string(),
});

export const baseSkillSchema = z.object({
    id: idSchema,
    isSynced: z.boolean().optional().default(true),
    skillType: skillTypesSchema,
    title: z.string(),
    titles: skillTitleTranslationSchema.array().readonly(),
});

export type BaseSkill = z.infer<typeof baseSkillSchema>;

export const baseSkillArraySchema = baseSkillSchema.array();

export const getSkillTitle = (skill: BaseSkill, languageCode?: string): string => {
    return skill.titles?.find((title) => title.language === languageCode)?.title ?? skill.title;
};

export const extendedSkillSchema = baseSkillSchema.extend({
    description: z.string().optional(),
});

export const hardSoftSkillSchema = baseSkillSchema.extend({
    isSynced: z.boolean().optional().default(true),
    level: hardSoftSkillProficiencyWithUnknownLevelSchema.optional().nullable(),
    skillType: z.union([z.literal('hard'), z.literal('soft')]),
});
export type HardSoftSkill = z.infer<typeof hardSoftSkillSchema>;
export type HardSoftSkillWithIconColor = HardSoftSkill & { readonly iconColor: string };

export const hardSkillSchema = hardSoftSkillSchema.extend({ skillType: z.literal('hard') });
export type HardSkill = z.infer<typeof hardSkillSchema>;

export const softSkillSchema = hardSoftSkillSchema.extend({ skillType: z.literal('soft') });
export type SoftSkill = z.infer<typeof softSkillSchema>;

export const languageSkillSchema = baseSkillSchema.extend({
    isSynced: z.boolean().optional().default(true),
    level: languageSkillProficiencyWithUnknownLevelSchema.optional().nullable(),
    skillType: z.literal('language'),
});
export type LanguageSkill = z.infer<typeof languageSkillSchema>;

export type LanguageSkillWithIconColor = LanguageSkill & { readonly iconColor: string };

export const skillSchema = z.union([hardSoftSkillSchema, languageSkillSchema]);
export type Skill = z.infer<typeof skillSchema>;

export type SkillWithIconColor = Skill & { readonly iconColor: string };

export const hardSoftSkillWithConfidenceSchema = hardSoftSkillSchema.extend({
    confidence: z.number().min(0).max(1).optional(),
});
export const languageSkillWithConfidenceSchema = languageSkillSchema.extend({
    confidence: z.number().min(0).max(1).optional(),
});

export const jobProfileSkillSchema = z.union([
    hardSoftSkillWithConfidenceSchema.extend({
        competencyId: z.string().optional(),
    }),
    languageSkillWithConfidenceSchema.extend({
        competencyId: z.string().optional(),
    }),
]);

export type JobProfileSkill = z.infer<typeof jobProfileSkillSchema>;

export const endorsedHardSoftSkillSchema = hardSoftSkillSchema.extend(endorsementSchema.shape);
export const endorsedHardSoftSkillArraySchema = endorsedHardSoftSkillSchema.array();
export type EndorsedHardSoftSkill = z.infer<typeof endorsedHardSoftSkillSchema>;

export const endorsedLanguageSkillSchema = languageSkillSchema.extend(endorsementSchema.shape);
export const endorsedLanguageSkillArraySchema = endorsedLanguageSkillSchema.array();
export type EndorsedLanguageSkill = z.infer<typeof endorsedLanguageSkillSchema>;

const hardSoftSkillMutationSchema = hardSoftSkillSchema.pick({ id: true, level: true });
const languageSkillMutationSchema = languageSkillSchema.pick({ id: true, level: true });

export type HardSoftSkillMutation = z.infer<typeof hardSoftSkillMutationSchema>;
export type LanguageSkillMutation = z.infer<typeof languageSkillMutationSchema>;

export const skillMutationSchema = z.union([hardSoftSkillMutationSchema, languageSkillMutationSchema]);

export type SkillMutation = z.infer<typeof skillMutationSchema>;

const skillMutationSourceSchema = z.union([
    z.literal('user'),
    z.literal('service'),
    z.literal('onboarding'),
    z.literal('cv_import'),
    z.literal('history_course'),
    z.literal('history_work'),
    z.literal('history_certificate'),
    z.literal('history_education'),
    z.literal('history_project'),
    z.literal('history_publication'),
    z.literal('skill_merge'),
    z.literal('skill_archive'),
]);

export type SkillMutationSource = z.infer<typeof skillMutationSourceSchema>;

export const hardSoftSkillShortSchema = baseSkillSchema.extend({
    level: hardSoftSkillProficiencyWithUnknownLevelSchema.optional().nullable(),
    skillType: z.union([z.literal('hard'), z.literal('soft')]),
});
export type HardSoftSkillShort = z.infer<typeof hardSoftSkillShortSchema>;

export const languageSkillShortSchema = baseSkillSchema.extend({
    level: languageSkillProficiencyWithUnknownLevelSchema.optional().nullable(),
    skillType: z.literal('language'),
});

export type LanguageSkillShort = z.infer<typeof languageSkillShortSchema>;

export const skillShortSchema = z.union([hardSoftSkillShortSchema, languageSkillShortSchema]);

export type SkillShort = z.infer<typeof skillShortSchema>;

export const skillsRecommendationSchema = z.object({
    hardSkillsLimit: z.number().min(0).optional(),
    languageSkillsLimit: z.number().min(0).optional(),
    skills: z
        .object({
            id: z.uuid(),
            level: z.number().optional(),
        })
        .array(),
    softSkillsLimit: z.number().min(0).optional(),
});

export type SkillsRecommendationRequest = z.infer<typeof skillsRecommendationSchema>;

const skillPresetSchema = z.object({
    hardSoftSkills: hardSoftSkillShortSchema.array(),
    languages: languageSkillShortSchema.array(),
    name: z.string(),
});

export type SkillPreset = z.infer<typeof skillPresetSchema>;
export const skillsPresetsSchema = z.object({
    presets: skillPresetSchema.array(),
});
export type SkillsPresets = z.infer<typeof skillsPresetsSchema>;

export type SkillsDetectionRequestDocType =
    | 'course'
    | 'cv'
    | 'history_course'
    | 'history_education'
    | 'history_project'
    | 'history_publication'
    | 'history_work'
    | 'job'
    | 'unknown';

/**
 * @property title - has a higher score in detection, but even acceptable to be skipped
 * @property text - source to detect skills
 * @property language - language of the source text
 */
export type SkillsDetectionRequest = {
    readonly language?: string;
    readonly text: string;
    readonly title?: string;
};

const skillsDetectionResponseSchema = z.object({
    hardSkills: hardSkillSchema
        .extend({
            competencyId: z.string().optional(),
        })
        .array(),
    languageSkills: languageSkillSchema
        .extend({
            competencyId: z.string().optional(),
        })
        .array(),
    softSkills: softSkillSchema
        .extend({
            competencyId: z.string().optional(),
        })
        .array(),
});

export const skillsDetectionRequestSchema = z.object({
    language: z.union([z.literal('de-DE'), z.literal('en-US'), z.literal('es-ES')]).optional(),
    text: z.string(),
    title: z.string().optional(),
});

export const skillsDetectionResponseTransformedSchema = skillsDetectionResponseSchema.transform((value) => [
    ...value.hardSkills,
    ...value.softSkills,
    ...value.languageSkills,
]);

export const skillsOptionsSchema = z.object({
    canDetectSkills: z.boolean(),
    canReadRelatedSkills: z.boolean(),
});

export type SkillsOptions = z.infer<typeof skillsOptionsSchema>;

export const architectureWithTitleSchema = z.object({
    id: idSchema,
    title: z.string(),
});

export const learningOpportunityWithTitleSchema = z.object({
    id: idSchema,
    title: z.string(),
});

export type ArchitectureWithTitle = z.infer<typeof architectureWithTitleSchema>;

export const skillSyncStatusSchema = z.union([
    z.literal('NOT_SYNCED'),
    z.literal('SYNC'),
    z.literal('NEEDS_REVIEW'),
    z.literal('SYNC_WHEN_IN_PROFILE'),
]);

export type SkillSyncStatus = z.infer<typeof skillSyncStatusSchema>;

export const jobArchitectureItemReferenceSchema = z.object({
    architectureId: idSchema,
    id: idSchema,
    isFromPublishedVersion: z.boolean(),
    title: z.string(),
});

export type JobArchitectureItemReference = z.infer<typeof jobArchitectureItemReferenceSchema>;

export const cappedJobArchitectureItemListSchema = z.object({
    items: jobArchitectureItemReferenceSchema.array().readonly(),
    totalCount: z.number(),
});

export type CappedJobArchitectureItemList = z.infer<typeof cappedJobArchitectureItemListSchema>;

export const skillGapSchema = z.object({
    absolute: z.number(),
    relative: z.number(),
});

export type SkillGap = z.infer<typeof skillGapSchema>;

export const jobArchitectureSkillSchema = baseSkillSchema.extend({
    addedAt: z.string().optional(),
    jobArchitecturesWithSkill: z.array(architectureWithTitleSchema),
    jobProfilesWithThisSkill: z.number(),
    jobsWithThisSkill: z.number(),
    learningOpportunitiesWithSkill: z.array(learningOpportunityWithTitleSchema),
    learningOpportunitiesWithThisSkill: z.number(),
    levels: cappedJobArchitectureItemListSchema,
    roles: cappedJobArchitectureItemListSchema,
    skillGap: skillGapSchema.optional(),
    syncStatus: skillSyncStatusSchema,
    usersWithThisSkill: z.number(),
});

export type JobArchitectureSkill = z.infer<typeof jobArchitectureSkillSchema>;

export const paginatedSkillCatalogSchema = z.object({
    data: jobArchitectureSkillSchema.array(),
    pagination: paginationSchema,
});

export type PaginatedSkillCatalog = z.infer<typeof paginatedSkillCatalogSchema>;

export const skillCatalogStatisticsSchema = z.object({
    deactivatedSkills: z.number(),
    skillsToReview: z.number(),
    syncedSkills: z.number(),
    totalSkills: z.number(),
});

export type SkillCatalogStatistics = z.infer<typeof skillCatalogStatisticsSchema>;

export const skillCatalogOptionsSchema = z.object({
    canManageSkillCatalog: z.boolean(),
    canReadSkillCatalog: z.boolean(),
});

export type SkillCatalogOptions = z.infer<typeof skillCatalogOptionsSchema>;
